import React, { useEffect, useState } from 'react';
import {
  Modal,
  Stack,
  Card,
  Group,
  Text,
  Button,
  Textarea,
  ScrollArea,
  Box,
  Divider,
  Alert,
  Image,
} from '@mantine/core';
import { IconUser, IconInfoCircle, IconBrain, IconCash } from '@tabler/icons-react';
import type { CharacterInfo, ModelInfo } from '@gofranz/gpt-muse-common';
import { getCharacterImagePath } from '../../lib/character-utils';
import { formatPricing } from '../../lib/model-utils';
import { getModelBadges } from '../../lib/model-ui-utils';

interface CharacterSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  selectedCharacter: string | null;
  onCharacterSelect: (characterId: string | null) => void;
  onModelSelect?: (modelId: string) => void;
  models: ModelInfo[];
  characters?: CharacterInfo[];
}

// Get recommended models for a specific character that are actually available
const getRecommendedModels = (models: ModelInfo[], character: CharacterInfo | undefined): string[] => {
  if (!character?.recommended_models) {
    return [];
  }

  return character.recommended_models.filter((modelId: string) =>
    models.some(model => model.id === modelId)
  );
};
const MAX_PROMPT_LENGTH = 2000;

export const CharacterSelectionModal = React.memo<CharacterSelectionModalProps>(({
  opened,
  onClose,
  selectedCharacter,
  onCharacterSelect,
  onModelSelect,
  models,
  characters = [],
}) => {
  const [error] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState('');
  const [showModelRecommendation, setShowModelRecommendation] = useState(false);
  const [pendingCharacterId, setPendingCharacterId] = useState<string | null>(null);
  const [customPromptError, setCustomPromptError] = useState<string | null>(null);

  // Load custom prompt on mount
  useEffect(() => {
    if (opened) {
      loadCustomPrompt();
    }
  }, [opened]);

  const loadCustomPrompt = async () => {
    try {
      const { chatDB } = await import('../../lib/chat-db');
      await chatDB.init();
      const settings = await chatDB.getGlobalSettings();
      
      if (settings?.customPrompt) {
        setCustomPrompt(settings.customPrompt);
      }
    } catch (error) {
      console.error('Failed to load custom prompt:', error);
    }
  };

  const handleSelectNoCharacter = () => {
    onCharacterSelect(null);
    onClose();
  };

  const handleSelectCharacter = (characterId: string) => {
    if (characterId === 'custom') {
      // For custom character, don't show model recommendation
      onCharacterSelect('custom');
      onClose();
    } else {
      // For API characters, show model recommendation
      setPendingCharacterId(characterId);
      setShowModelRecommendation(true);
    }
  };

  const handleModelRecommendation = (modelId: string | null) => {
    if (modelId && onModelSelect) {
      onModelSelect(modelId);
    }
    
    // Complete character selection
    onCharacterSelect(pendingCharacterId);
    setShowModelRecommendation(false);
    setPendingCharacterId(null);
    onClose();
  };

  const handleCustomPromptSave = () => {
    // Validate prompt
    if (customPrompt.trim().length === 0) {
      setCustomPromptError('Please enter a custom prompt');
      return;
    }

    if (customPrompt.length > MAX_PROMPT_LENGTH) {
      setCustomPromptError(`Prompt is too long. Maximum ${MAX_PROMPT_LENGTH} characters allowed.`);
      return;
    }

    onCharacterSelect('custom');
    onClose();
  };

  const handleClose = () => {
    setShowModelRecommendation(false);
    setPendingCharacterId(null);
    setCustomPromptError(null);
    onClose();
  };

  const getRecommendedModelInfo = (modelId: string): ModelInfo | undefined => {
    return models.find(m => m.id === modelId);
  };

  const remainingChars = MAX_PROMPT_LENGTH - customPrompt.length;
  const isOverLimit = remainingChars < 0;

  if (showModelRecommendation) {
    const pendingCharacterInfo = characters.find(c => c.id === pendingCharacterId);
    const recommendedModelIds = getRecommendedModels(models, pendingCharacterInfo);

    return (
      <Modal
        opened={opened}
        onClose={handleClose}
        title={
          <Group gap="xs">
            <IconBrain size={20} />
            <Text size="lg" fw={600}>Select Recommended Model</Text>
          </Group>
        }
        size="lg"
        centered
      >
        <Stack gap="md">
          <Alert icon={<IconInfoCircle size={16} />} color="blue">
            You selected <strong>{pendingCharacterInfo?.name}</strong>.&nbsp;
            {recommendedModelIds.length > 0
              ? 'Choose a recommended model for the best experience, or continue with your current selection.'
              : 'No specific model recommendations available for this character. You can continue with your current model.'
            }
          </Alert>

          <ScrollArea h={300} offsetScrollbars>
            <Stack gap="sm">
              {recommendedModelIds.map((modelId) => {
                const modelInfo = getRecommendedModelInfo(modelId);
                if (!modelInfo) return null;

                return (
                  <Card
                    key={modelId}
                    p="md"
                    withBorder
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleModelRecommendation(modelId)}
                  >
                    <Stack gap="sm">
                      <Group justify="space-between" align="flex-start" wrap="nowrap">
                        <Box style={{ flex: 1, minWidth: 0 }}>
                          <Text size="md" fw={600} truncate>
                            {modelInfo.name}
                          </Text>
                          <Text size="xs" c="dimmed" truncate>
                            {modelInfo.id}
                          </Text>
                        </Box>

                        <Group gap={4} wrap="nowrap">
                          {getModelBadges(modelInfo)}
                        </Group>
                      </Group>

                      <Text size="sm" c="dimmed" lineClamp={2}>
                        {modelInfo.description}
                      </Text>

                      <Divider />

                      <Group gap="lg" wrap="nowrap">
                        <Group gap={4} align="center" style={{ flex: 1, minWidth: 0 }}>
                          <IconCash size={14} color="gray" />
                          <Text size="xs" c="dimmed" truncate>
                            {formatPricing(modelInfo)}
                          </Text>
                        </Group>

                        <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                          {modelInfo.provider_count} provider{modelInfo.provider_count !== 1 ? 's' : ''}
                        </Text>
                      </Group>
                    </Stack>
                  </Card>
                );
              })}

              <Card
                p="md"
                withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => handleModelRecommendation(null)}
              >
                <Text size="md" fw={500}>
                  Continue with current model
                </Text>
                <Text size="sm" c="dimmed">
                  Keep using your currently selected model
                </Text>
              </Card>
            </Stack>
          </ScrollArea>

          <Group justify="flex-end">
            <Button variant="light" onClick={handleClose}>
              Cancel
            </Button>
          </Group>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconUser size={20} />
          <Text size="lg" fw={600}>Select Character</Text>
        </Group>
      }
      size="lg"
      centered
    >
      <Stack gap="md">
        <ScrollArea h={400} offsetScrollbars>
          <Stack gap="xs">
            {/* No character option */}
            <Card
              p="md"
              withBorder
              style={{
                cursor: 'pointer',
                border: selectedCharacter === null ? '2px solid var(--mantine-primary-color-filled)' : undefined,
                backgroundColor: selectedCharacter === null ? 'var(--mantine-primary-color-light)' : undefined,
              }}
              onClick={handleSelectNoCharacter}
            >
              <Text size="md" fw={500} c="dimmed">
                No character
              </Text>
              <Text size="sm" c="dimmed">
                Use the model without any character personality
              </Text>
            </Card>

            {/* Error state */}
            {error && (
              <Text size="sm" c="red" ta="center" py="xl">
                {error}
              </Text>
            )}

            {/* Character options */}
            {characters.map((character) => (
              <Card
                key={character.id}
                p="md"
                withBorder
                style={{
                  cursor: 'pointer',
                  border: selectedCharacter === character.id ? '2px solid var(--mantine-primary-color-filled)' : undefined,
                  backgroundColor: selectedCharacter === character.id ? 'var(--mantine-primary-color-light)' : undefined,
                }}
                onClick={() => handleSelectCharacter(character.id)}
              >
                <Group gap="md" align="flex-start">
                  <Image
                    src={getCharacterImagePath(character.id)}
                    alt={character.name}
                    w={60}
                    h={60}
                    radius="md"
                    fallbackSrc="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjYwIiBoZWlnaHQ9IjYwIiByeD0iOCIgZmlsbD0iIzg4ODg4OCIvPgo8cGF0aCBkPSJNMzAgMjBDMzIuNzYxNCAyMCAzNSAyMi4yMzg2IDM1IDI1QzM1IDI3Ljc2MTQgMzIuNzYxNCAzMCAzMCAzMEMyNy4yMzg2IDMwIDI1IDI3Ljc2MTQgMjUgMjVDMjUgMjIuMjM4NiAyNy4yMzg2IDIwIDMwIDIwWiIgZmlsbD0id2hpdGUiLz4KPHA + CzMwIDQ1QzM1IDQ1IDQwIDQyIDQwIDM3QzQwIDM1IDMzIDMyIDMwIDMyQzI3IDMyIDIwIDM1IDIwIDM3QzIwIDQyIDI1IDQ1IDMwIDQ1WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+"
                    style={{ flexShrink: 0 }}
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="md" fw={600}>
                      {character.name}
                    </Text>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {character.description}
                    </Text>
                    <Text size="sm" c="dimmed" lineClamp={2} mt={4}>
                      {character.description_long}
                    </Text>
                  </Box>
                </Group>
              </Card>
            ))}

            {/* Custom prompt option */}
            <Card
              p="md"
              withBorder
              style={{
                border: selectedCharacter === 'custom' ? '2px solid var(--mantine-primary-color-filled)' : undefined,
                backgroundColor: selectedCharacter === 'custom' ? 'var(--mantine-primary-color-light)' : undefined,
              }}
            >
              <Group justify="space-between" mb="md">
                <Box>
                  <Text size="md" fw={600}>
                    ✏️ Write custom prompt
                  </Text>
                  <Text size="sm" c="dimmed">
                    Create your own custom system prompt
                  </Text>
                </Box>
              </Group>

              <Divider my="sm" />

              <Stack gap="xs">
                <Textarea
                  placeholder="Enter your custom system prompt here..."
                  value={customPrompt}
                  onChange={(e) => {
                    setCustomPrompt(e.target.value);
                    setCustomPromptError(null);
                  }}
                  minRows={4}
                  maxRows={8}
                  autosize
                  error={customPromptError}
                />
                <Group justify="space-between">
                  <Text 
                    size="xs" 
                    c={isOverLimit ? "red" : "dimmed"}
                  >
                    {customPrompt.length} / {MAX_PROMPT_LENGTH} characters
                  </Text>
                  {remainingChars < 100 && remainingChars >= 0 && (
                    <Text size="xs" c="orange">
                      {remainingChars} characters remaining
                    </Text>
                  )}
                  {isOverLimit && (
                    <Text size="xs" c="red" fw={500}>
                      {Math.abs(remainingChars)} characters over limit
                    </Text>
                  )}
                </Group>
                <Button
                  size="sm"
                  onClick={handleCustomPromptSave}
                  disabled={isOverLimit || customPrompt.trim().length === 0}
                  fullWidth
                >
                  Use Custom Prompt
                </Button>
              </Stack>
            </Card>
          </Stack>
        </ScrollArea>

        <Group justify="flex-end">
          <Button variant="light" onClick={handleClose}>
            Cancel
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
});

CharacterSelectionModal.displayName = 'CharacterSelectionModal';